import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, File, X, AlertCircle, Check } from 'lucide-react';
import { cn } from '../lib/utils';

const FileUpload = ({ 
  onFileSelect, 
  accept = '.pdf,.docx', 
  maxSize = 5 * 1024 * 1024, // 5MB
  required = false,
  label = "Upload File",
  description = "Drag & drop or click to select",
  className 
}) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [error, setError] = useState('');

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setError('');

    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors.some(error => error.code === 'file-too-large')) {
        setError(`File too large. Maximum size: ${maxSize / (1024 * 1024)}MB`);
      } else if (rejection.errors.some(error => error.code === 'file-invalid-type')) {
        setError('Invalid file type. Please upload PDF or DOCX files only.');
      } else {
        setError('File upload failed. Please try again.');
      }
      return;
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setUploadedFile(file);
      onFileSelect(file);
    }
  }, [onFileSelect, maxSize]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxSize,
    multiple: false
  });

  const removeFile = () => {
    setUploadedFile(null);
    setError('');
    onFileSelect(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={cn("space-y-2", className)}>
      <label className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      {!uploadedFile ? (
        <div
          {...getRootProps()}
          className={cn(
            "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
            isDragActive ? "border-blue-400 bg-blue-50" : "border-gray-300 hover:border-gray-400",
            error && "border-red-300 bg-red-50"
          )}
        >
          <input {...getInputProps()} />
          <Upload className={cn(
            "mx-auto h-12 w-12 mb-4",
            isDragActive ? "text-blue-400" : "text-gray-400"
          )} />
          <p className="text-sm text-gray-600 mb-2">
            {isDragActive ? "Drop the file here..." : description}
          </p>
          <p className="text-xs text-gray-500">
            Supports: PDF, DOCX (max {maxSize / (1024 * 1024)}MB)
          </p>
        </div>
      ) : (
        <div className="border rounded-lg p-4 bg-green-50 border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <File className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-green-800">
                  {uploadedFile.name}
                </p>
                <p className="text-xs text-green-600">
                  {formatFileSize(uploadedFile.size)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Check className="h-5 w-5 text-green-600" />
              <button
                onClick={removeFile}
                className="p-1 hover:bg-green-200 rounded-full transition-colors"
              >
                <X className="h-4 w-4 text-green-600" />
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center space-x-2 text-red-600 text-sm">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default FileUpload;